import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BubbleInput, BubbleButton, BubbleAlert } from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import { mapBy } from '@go-shared/lib/functional';
import {
  AttributeTranslation,
  AttributeTransforms,
  AttributeTranslatable,
} from '@go-shared/types/translation';
import { replaceTranslationByRefer, getTranslations } from '@go-client/lib/api/translation';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import Fuse from 'fuse.js';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TranslationBlock from '@go-client/components/TranslationBlock';
import Translations from './Translations';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  generateDefaultNewTranslation,
  makeAttributeTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandAttribute } from '@go-shared/types/brand';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canModifyBrandTranslation } from '@go-shared/lib/permissions';

interface AttributeTranslationDataBundle {
  attributes: BrandAttribute[];
  attributeTranslations: AttributeTranslation[];
  attributesMap: { [key: string]: BrandAttribute };
  attributeTranslationsMap: { [key: string]: AttributeTranslation };
}

interface AttributeTranslationForm {
  dirty: boolean;
  transforms: AttributeTransforms;
}

interface ChangeAttributeFormAction {
  active?: boolean;
  valueChange?: { key: keyof AttributeTranslatable; value: string };
}

type AttributeTranslationsPageProps = RouteComponentProps;

export default function AttributeTranslations(props: AttributeTranslationsPageProps) {
  const [loading, setLoading] = useState(true);
  const [dataBundle, setDataBundle] = useState<AttributeTranslationDataBundle | undefined>(
    undefined
  );
  const [activeAttribute, setActiveAttribute] = useState<BrandAttribute | undefined>(undefined);
  const [attributeTranslationForm, setAttributeTranslationForm] = useState<
    AttributeTranslationForm | undefined
  >(undefined);
  const [search, setSearch] = useState('');

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { activeProperty } = useActiveProperty();

  const serverInfo = useServerInfo();
  const { brand, languages } = serverInfo;
  const { user } = useUserInfo();

  useEffect(() => {
    setLoading(true);
    fetchDataBundle().catch(console.error);
  }, [activeProperty]);

  // this will refresh the currently active attribute if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;

    setActiveAttribute(state => {
      const brandAttribute = dataBundle.attributes[0];
      if (!state) return brandAttribute;
      const refreshedAttribute = dataBundle.attributesMap[state._id];
      return refreshedAttribute || brandAttribute;
    });
  }, [dataBundle]);

  // this will reset the form each time a new attribute is clicked
  useEffect(() => {
    resetAttributesForm();
  }, [activeAttribute]);

  // this will scroll the page to top when search is modified
  useEffect(() => {
    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [search, contentRef]);

  const fuse = useMemo(() => {
    if (!dataBundle) return undefined;

    return new Fuse(dataBundle.attributes, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [dataBundle]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredAttributes = !dataBundle ? [] : fuseSearchResults || dataBundle.attributes;

  async function fetchDataBundle() {
    try {
      const attributes = brand.attributes;
      const attributeTranslations = await getTranslations('attribute');

      const attributesMap = mapBy(attributes, '_id');
      const attributeTranslationsMap = mapBy(attributeTranslations, 'refers');

      setDataBundle({
        attributes,
        attributeTranslations,
        attributesMap,
        attributeTranslationsMap,
      });
    } finally {
      setLoading(false);
    }
  }

  function resetAttributesForm() {
    const transforms = activeAttribute
      ? makeAttributeTranslationTransform(
          languages.map(l => l.key),
          dataBundle?.attributeTranslationsMap[activeAttribute._id]
        )
      : undefined;

    if (!transforms) {
      setAttributeTranslationForm(undefined);
      return;
    }

    const formData: AttributeTranslationForm = {
      dirty: false,
      transforms,
    };

    setAttributeTranslationForm(formData);
  }

  function changeAttributeForm(lang: string, change: ChangeAttributeFormAction) {
    if (!attributeTranslationForm) return;

    const updatedForm = { ...attributeTranslationForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setAttributeTranslationForm(updatedForm);
  }

  async function handleSubmitAttributeForm() {
    if (
      !attributeTranslationForm ||
      !activeAttribute ||
      !dataBundle ||
      !canModifyBrandTranslation(user, serverInfo)
    )
      return;

    const translation = generateDefaultNewTranslation(activeAttribute._id, 'attribute');

    translation.transforms = attributeTranslationForm.transforms;

    await replaceTranslationByRefer(activeAttribute._id, translation);
    await fetchDataBundle();
    setAttributeTranslationForm({ ...attributeTranslationForm, dirty: false });
  }

  function headerFn() {
    return <TranslationsSubNavigation active="attributes" property={activeProperty} />;
  }

  function leftFn() {
    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleInput
            type="text"
            block
            appearance="bare"
            placeholder="Search..."
            className="flex-grow"
            inputStyle={{ height: '49px' }}
            inputClassName="px-5"
            value={search}
            onValue={setSearch}
          />
        </div>
        <div style={{ paddingTop: '50px' }}>
          {filteredAttributes.map(attribute => (
            <div
              key={attribute._id}
              className={`list-item px-5 py-3 cursor-pointer ${
                activeAttribute && safeIdCompare(activeAttribute._id, attribute._id) ? 'active' : ''
              }`}
              onClick={() => setActiveAttribute(attribute)}
            >
              <div className="flex items-center">
                <div className="overflow-ellipsis">
                  <div className="list-item-name font-semibold">{attribute.name}</div>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                />
              </div>
            </div>
          ))}
          {filteredAttributes.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No attributes found
            </div>
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeAttribute && attributeTranslationForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyBrandTranslation(user, serverInfo) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This attribute cannot be modified unless you're in a brand instance and a brand
                  admin.
                </BubbleAlert>
              )}
              <TranslationBlock
                label="Name"
                value={activeAttribute.name}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({attributeTranslationForm.transforms[language.key].delta.name.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !attributeTranslationForm.transforms[language.key].active ? 'smoke' : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeAttributeForm(language.key, {
                          active: !attributeTranslationForm.transforms[language.key].active,
                        })
                      }
                      icon={
                        attributeTranslationForm.transforms[language.key].active
                          ? 'eye'
                          : 'eye-slash'
                      }
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !attributeTranslationForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={attributeTranslationForm.transforms[language.key].delta.name}
                      onValue={val =>
                        changeAttributeForm(language.key, {
                          valueChange: { key: 'name', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            </div>

            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitAttributeForm()}
                variant="navy"
                disabled={
                  !attributeTranslationForm.dirty || !canModifyBrandTranslation(user, serverInfo)
                }
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!attributeTranslationForm.dirty}
                inverse
                onClick={() => resetAttributesForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (attributeTranslationForm && attributeTranslationForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={attributeTranslationForm && attributeTranslationForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        category="attributes"
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
      />
    </>
  );
}
